/**
 * useAssistant — SSE streaming hook for the chat sidebar.
 * Sends POST to /api/intel/chat, reads SSE stream, updates messages state.
 */

import { useCallback, useRef, useState } from "react";
import type { PageContext } from "./PageContextProvider.js";
import type { ChatAssistant, ChatUIMessage } from "./types.js";

export interface UseAssistantOptions {
  /** Default sticky for new conversations. Backend resolves final routing. */
  defaultAssistant?: ChatAssistant;
  pageContext?: PageContext | null;
  conversationId?: string | null;
  onConversationChange?: (id: string) => void;
}

export interface UseAssistantReturn {
  messages: ChatUIMessage[];
  send: (text: string) => Promise<void>;
  streaming: boolean;
  currentResponse: string;
  conversationId: string | null;
  currentAssistant: ChatAssistant;
  clearMessages: () => void;
}

export function useAssistant({
  defaultAssistant = "jared",
  pageContext,
  conversationId: initialConversationId,
  onConversationChange,
}: UseAssistantOptions): UseAssistantReturn {
  const [messages, setMessages] = useState<ChatUIMessage[]>([]);
  const [streaming, setStreaming] = useState(false);
  const [currentResponse, setCurrentResponse] = useState("");
  const [conversationId, setConversationId] = useState<string | null>(
    initialConversationId ?? null,
  );
  const [currentAssistant, setCurrentAssistant] = useState<ChatAssistant>(defaultAssistant);
  const abortRef = useRef<AbortController | null>(null);

  const send = useCallback(
    async (text: string) => {
      if (!text.trim() || streaming) return;

      // Add user message optimistically
      const userMsg: ChatUIMessage = {
        id: `user-${Date.now()}`,
        role: "user",
        content: text,
        created_at: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, userMsg]);
      setStreaming(true);
      setCurrentResponse("");

      // Abort any previous request
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      const assistantMsgId = `assistant-${Date.now()}`;
      let accumulated = "";

      try {
        const res = await fetch("/api/intel/chat", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            message: text,
            conversation_id: conversationId,
            page_context: pageContext ?? undefined,
          }),
          signal: controller.signal,
        });

        if (!res.ok) {
          const errText = await res.text().catch(() => `HTTP ${res.status}`);
          throw new Error(errText);
        }

        const reader = res.body?.getReader();
        if (!reader) throw new Error("No response body");

        const decoder = new TextDecoder();

        // Add streaming placeholder
        setMessages((prev) => [
          ...prev,
          { id: assistantMsgId, role: "assistant", content: "", streaming: true },
        ]);

        let buffer = "";

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });

          // Parse SSE lines
          const lines = buffer.split("\n");
          buffer = lines.pop() ?? "";

          for (const line of lines) {
            if (line.startsWith("event: ")) continue; // event type line
            if (!line.startsWith("data: ")) continue;

            const rawData = line.slice(6);
            if (rawData === "[DONE]") break;

            try {
              const parsed = JSON.parse(rawData) as {
                event?: string;
                data?: string;
                conversation_id?: string;
                assistant?: string;
                error?: string;
              };

              if (parsed.event === "text" || !parsed.event) {
                const chunk = parsed.data ?? "";
                accumulated += chunk;
                setCurrentResponse(accumulated);
                setMessages((prev) =>
                  prev.map((m) =>
                    m.id === assistantMsgId ? { ...m, content: accumulated, streaming: true } : m,
                  ),
                );
              } else if (parsed.event === "done") {
                const convId = parsed.conversation_id ?? parsed.data;
                if (convId && !conversationId) {
                  setConversationId(convId);
                  onConversationChange?.(convId);
                }
                if (parsed.assistant) {
                  setCurrentAssistant(parsed.assistant as ChatAssistant);
                }
              } else if (parsed.event === "error") {
                throw new Error(parsed.data ?? "Streaming error");
              }
            } catch (innerErr) {
              if (!(innerErr instanceof SyntaxError)) {
                // Intentional throw (e.g. event === "error") — propagate to outer handler
                throw innerErr;
              }
              // Non-JSON SSE data — treat as raw text chunk
              accumulated += rawData;
              setCurrentResponse(accumulated);
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantMsgId ? { ...m, content: accumulated, streaming: true } : m,
                ),
              );
            }
          }
        }

        // Finalize message (remove streaming flag)
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantMsgId ? { ...m, content: accumulated, streaming: false } : m,
          ),
        );
      } catch (err) {
        if ((err as Error).name === "AbortError") return;
        const errorMsg = err instanceof Error ? err.message : String(err);
        setMessages((prev) => {
          const hasPlaceholder = prev.some((m) => m.id === assistantMsgId);
          if (hasPlaceholder) {
            return prev.map((m) =>
              m.id === assistantMsgId ? { ...m, content: errorMsg, streaming: false } : m,
            );
          }
          // Placeholder was never added (e.g. HTTP error before stream started)
          return [
            ...prev,
            {
              id: assistantMsgId,
              role: "assistant" as const,
              content: errorMsg,
              streaming: false,
              created_at: new Date().toISOString(),
            },
          ];
        });
      } finally {
        setStreaming(false);
        setCurrentResponse("");
      }
    },
    [pageContext, conversationId, streaming, onConversationChange],
  );

  const clearMessages = useCallback(() => {
    abortRef.current?.abort();
    setMessages([]);
    setCurrentResponse("");
    setStreaming(false);
    setConversationId(null);
    setCurrentAssistant(defaultAssistant);
  }, [defaultAssistant]);

  return {
    messages,
    send,
    streaming,
    currentResponse,
    conversationId,
    currentAssistant,
    clearMessages,
  };
}
